import { Core } from "@faire/design-tokens";
import { getExtension } from "@faire/help-center-utils/lib/files";
import { formatBytes } from "@faire/web--source/common/formatBytes";
import {
  StrictLocalizeFunction,
  backwardsCompatibleValues,
} from "@faire/web--source/common/localization";
import { FileRejection } from "react-dropzone";

import { IFileUploaderDropzoneProps } from "./FileUploaderDropzone";

export const useFileUploaderDropzoneState = ({
  onUploadSuccess,
  onUploadError,
  accepts,
  maxFileSize,
  currentFile,
  loading,
  error,
  strictLocalize,
}: IFileUploaderDropzoneProps) => {
  const handleAcceptedFile = async (acceptedFile: File) => {
    try {
      await onUploadSuccess(acceptedFile);
    } catch {
      onUploadError(UPLOADING_ERROR_MESSAGE(acceptedFile, strictLocalize));
    }
  };

  const handleRejectedFile = (rejection: FileRejection) => {
    const baseMessage = BASE_MESSAGE(rejection.file, strictLocalize);
    if (rejection.file.size > maxFileSize) {
      const fileSizeMessage = FILE_SIZE_ERROR_MESSAGE(
        maxFileSize,
        strictLocalize
      );
      onUploadError(`${baseMessage} ${fileSizeMessage}`);
    } else if (!accepts.includes(rejection.file.type)) {
      const fileTypeMessage = FILE_TYPE_ERROR_MESSAGE(
        rejection.file,
        strictLocalize
      );
      onUploadError(`${baseMessage} ${fileTypeMessage}`);
    } else {
      const defaultMessage = DEFAULT_ERROR_MESSAGE(strictLocalize);
      onUploadError(`${baseMessage} ${defaultMessage}`);
    }
  };

  const handleDropResult = (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[]
  ) => {
    acceptedFiles.forEach((acceptedFile) => handleAcceptedFile(acceptedFile));
    rejectedFiles.forEach((rejectedFile) => handleRejectedFile(rejectedFile));
  };

  const showChosenFile: boolean = !!currentFile && !loading;
  const showUploadError: boolean = !currentFile && !!error;
  const iconColor: string = showUploadError
    ? Core.text.critical
    : Core.text.subdued;

  return {
    handleDropResult,
    showChosenFile,
    showUploadError,
    iconColor,
  };
};

const BASE_MESSAGE = (file: File, strictLocalize: StrictLocalizeFunction) => {
  return strictLocalize(
    {
      defaultMessage: "Error uploading {fileName}.",
      description: {
        text: "Indicating there was an error uploading a file",
      },
    },
    backwardsCompatibleValues({ fileName: file.name })
  );
};

const UPLOADING_ERROR_MESSAGE = (
  file: File,
  strictLocalize: StrictLocalizeFunction
) => {
  return strictLocalize(
    {
      defaultMessage: "Error uploading {fileName}. Please try again.",
    },
    backwardsCompatibleValues({ fileName: file.name })
  );
};

const FILE_SIZE_ERROR_MESSAGE = (
  maxSize: number,
  strictLocalize: StrictLocalizeFunction
) => {
  return strictLocalize(
    {
      defaultMessage: "Please upload a file less than {fileSize}.",
      description: {
        text: "Telling user to upload a file that is a smaller size",
      },
    },
    backwardsCompatibleValues({
      fileSize: formatBytes(maxSize, strictLocalize),
    })
  );
};

const FILE_TYPE_ERROR_MESSAGE = (
  file: File,
  strictLocalize: StrictLocalizeFunction
) => {
  return strictLocalize(
    {
      defaultMessage: '"{fileType}" is not a supported file type.',
      description: {
        text: "Telling user to upload a file that is a different file type",
      },
    },
    backwardsCompatibleValues({ fileType: getExtension(file) })
  );
};

const DEFAULT_ERROR_MESSAGE = (strictLocalize: StrictLocalizeFunction) => {
  return strictLocalize({
    defaultMessage: "Please try again.",
    description: {
      text: "Telling user to try uploading their file again",
    },
  });
};
